import { useState } from "react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowLeftRight, ChevronDown, GripVertical, LayoutGrid, RotateCcw } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

export const DEFAULT_LAYOUT = { left: ["notes", "calls", "timeline"], right: ["analyse", "contacts", "phones"], collapsed: [] };
const LABELS = { notes: "Add note", calls: "Insight calls", timeline: "History", analyse: "AI analysis", contacts: "Contacts", phones: "Phone numbers" };

const normalise = (l) => {
  const all = Object.keys(LABELS);
  const left = (l?.left || DEFAULT_LAYOUT.left).filter((k) => all.includes(k));
  const right = (l?.right || DEFAULT_LAYOUT.right).filter((k) => all.includes(k) && !left.includes(k));
  const missing = all.filter((k) => !left.includes(k) && !right.includes(k));
  return { left, right: [...right, ...missing], collapsed: l?.collapsed || [] };
};

const Block = ({ id, editing, collapsed, onToggle, onMove, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !editing });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 30 : undefined }}
      className={`relative ${editing ? "rounded-2xl ring-1 ring-dashed ring-cyan-400/40" : ""} ${isDragging ? "opacity-80" : ""}`} data-testid={`layout-block-${id}`}>
      <div className="mb-1.5 flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {editing && <button {...attributes} {...listeners} className="cursor-grab touch-none active:cursor-grabbing" data-testid={`layout-drag-${id}`}><GripVertical className="h-4 w-4" /></button>}
        <button onClick={onToggle} className="flex items-center gap-1 hover:text-foreground" data-testid={`layout-collapse-${id}`}>
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${collapsed ? "-rotate-90" : ""}`} /> {LABELS[id]}
        </button>
        {editing && <button onClick={onMove} className="ml-auto flex items-center gap-1 hover:text-foreground" data-testid={`layout-move-${id}`}><ArrowLeftRight className="h-3.5 w-3.5" /> Other side</button>}
      </div>
      {!collapsed && <div className="scrollbar-thin max-h-[640px] overflow-y-auto rounded-2xl">{children}</div>}
    </div>
  );
};

export const SalesLayoutGrid = ({ sections }) => {
  const { prefs, savePrefs } = useSettings();
  const [editing, setEditing] = useState(false);
  const layout = normalise(prefs.sales_layout);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const save = (l) => savePrefs({ sales_layout: l });
  const onDragEnd = (side) => ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const list = layout[side];
    save({ ...layout, [side]: arrayMove(list, list.indexOf(active.id), list.indexOf(over.id)) });
  };
  const move = (id) => {
    const from = layout.left.includes(id) ? "left" : "right";
    const to = from === "left" ? "right" : "left";
    save({ ...layout, [from]: layout[from].filter((k) => k !== id), [to]: [...layout[to], id] });
  };
  const toggle = (id) => save({ ...layout, collapsed: layout.collapsed.includes(id) ? layout.collapsed.filter((k) => k !== id) : [...layout.collapsed, id] });
  const column = (side, cls) => (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd(side)}>
      <SortableContext items={layout[side]} strategy={verticalListSortingStrategy}>
        <div className={`min-h-[60px] space-y-6 ${cls}`} data-testid={`layout-column-${side}`}>
          {layout[side].map((id) => (
            <Block key={id} id={id} editing={editing} collapsed={layout.collapsed.includes(id)} onToggle={() => toggle(id)} onMove={() => move(id)}>{sections[id]}</Block>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
  return (
    <div className="mt-6">
      <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
        {editing && <button onClick={() => save(DEFAULT_LAYOUT)} className="flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid="layout-reset"><RotateCcw className="h-3.5 w-3.5" /> Reset</button>}
        <button onClick={() => setEditing((e) => !e)} className={`flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${editing ? "border-cyan-400 bg-cyan-400 text-black" : "text-muted-foreground hover:text-foreground"}`} data-testid="layout-edit-toggle">
          <LayoutGrid className="h-3.5 w-3.5" /> {editing ? "Done arranging" : "Arrange this page"}
        </button>
      </div>
      {editing && <p className="mb-4 text-xs text-muted-foreground">Drag sections by the handle, move them to the other side, or collapse them. Saved for your login on every company.</p>}
      <div className="grid gap-6 lg:grid-cols-12">
        {column("left", "lg:col-span-7")}
        {column("right", "lg:col-span-5")}
      </div>
    </div>
  );
};
